/** Same pattern as report delivery recipients (`EditDelivery.js`). */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Rejects whitespace and angle brackets in the address so malformed strings
 * (e.g. `a<b@c.d`, `a @b.c`) are not accepted.
 */
export function isStrictEmailAddress(email) {
  const e = String(email ?? '').trim();
  if (!e) return false;
  if (/[\s<>]/.test(e)) return false;
  return EMAIL_REGEX.test(e);
}

function splitDisplayName(namePart) {
  if (!namePart) return { firstname: undefined, lastname: undefined };
  const name = namePart.trim();
  if ((name.startsWith('"') && name.endsWith('"')) || (name.startsWith("'") && name.endsWith("'"))) {
    return { firstname: name.slice(1, -1), lastname: undefined };
  }
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstname: undefined, lastname: undefined };
  if (parts.length === 1) return { firstname: parts[0], lastname: undefined };
  return { firstname: parts[0], lastname: parts.slice(1).join(' ') };
}

/**
 * Parses `Display Name <email@domain>` or a plain email. Trims the address for lookup.
 * @returns {{ email: string, firstname?: string, lastname?: string }}
 */
export function parseInviteEmailInput(raw) {
  const trimmed = String(raw ?? '').trim();
  const match = trimmed.match(/^(.+?)\s*<([^>]+)>\s*$/);
  if (match) {
    let namePart = match[1].trim();
    if ((namePart.startsWith('"') && namePart.endsWith('"')) || (namePart.startsWith("'") && namePart.endsWith("'"))) {
      namePart = namePart.slice(1, -1);
    }
    const email = match[2].trim();
    const { firstname, lastname } = splitDisplayName(namePart);
    return { email, firstname, lastname };
  }
  return {
    email: trimmed,
    firstname: undefined,
    lastname: undefined,
  };
}

export function isValidInviteEmailRaw(raw) {
  const { email } = parseInviteEmailInput(raw);
  return isStrictEmailAddress(email);
}

/** Plain email for storage/API (invite + report recipients). */
export function normalizeRecipientEmailInput(raw) {
  const { email } = parseInviteEmailInput(String(raw ?? '').trim());
  return email.trim().toLowerCase();
}

/**
 * Split pasted multi-recipient text: `;` or newlines between entries; comma only when the segment has no `<…>` (plain lists).
 */
export function splitRecipientInputLines(raw) {
  return String(raw ?? '')
    .split(/[;\r\n]+/)
    .flatMap((segment) => {
      const s = segment.trim();
      if (!s) return [];
      if (/<[^>]+>/.test(s)) return [s];
      return s.split(',').map(x => x.trim()).filter(Boolean);
    });
}
